import type { Provider } from '@angular/core';
import { inject, isDevMode } from '@angular/core';
import { AmlAdminDeskTranslationService } from '@core/services/aml-admin-translation.service';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import type { Language } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import { LanguageMap } from '@kerberos-compliance/compliance-shared-ts/common/i18n/enums/language.enums';
import {
  AmlSharedTranslationService,
  localeMap,
  TRANSLATION_PROVIDER,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import type { NzI18nInterface } from 'ng-zorro-antd/i18n';
import { en_GB, NZ_I18N } from 'ng-zorro-antd/i18n';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './transloco-loader';

/**
 * Provides the NzI18n configuration based on the current active language from the TranslocoService.
 *
 * This function retrieves the active language from TranslocoService and maps it to the corresponding
 * Ng-Zorro locale configuration using a predefined locale map. If the active language does not exist
 * in the locale map, it defaults to the `en_GB` locale.
 *
 * @param {TranslocoService} translocoService - The Transloco service used for managing translations
 * and retrieving the active language.
 * @returns {NzI18nInterface} The Ng-Zorro i18n interface configuration corresponding to the active language.
 */
const provideNzI18nWithTransloco = (translocoService: TranslocoService): NzI18nInterface => {
  const currentLanguage = translocoService.getActiveLang() as Language;
  return localeMap[currentLanguage] || en_GB;
};

/**
 * Provides a shared UI library translation service with reactive language updates.
 *
 * This function returns a provider configuration for dependency injection,
 * which instantiates and manages the `AmlSharedTranslationService`. The provided
 * service synchronizes its active language setting with the `AmlAdminDeskTranslationService`.
 *
 * The `AmlSharedTranslationService` updates its language property both upon
 * initialization and reactively whenever the `AmlAdminDeskTranslationService` emits
 * a new active language.
 *
 * @returns {Provider} The provider configuration used to register the shared translation service.
 */
const provideSharedUiLibraryTranslation = (): Provider => {
  return {
    provide: AmlSharedTranslationService,
    useFactory: (appLanguageService: AmlAdminDeskTranslationService): AmlSharedTranslationService => {
      const translationService = new AmlSharedTranslationService();
      translationService.language = appLanguageService.activeLanguage();
      appLanguageService.activeLanguage$.subscribe((language) => {
        translationService.language = language;
      });
      return translationService;
    },
    deps: [AmlAdminDeskTranslationService],
  };
};

/**
 * `preloadDefaultLanguage` is a function that returns another function responsible for preloading
 * the default language in a Transloco-based i18n setup.
 *
 * The returned function, when executed, asynchronously loads the active language using the TranslocoService.
 *
 * @returns {(() => Promise<void>)} A function that, when invoked, returns a Promise resolved after the default language is loaded.
 */
export const preloadDefaultLanguage = (): (() => Promise<void>) => {
  return async (): Promise<void> => {
    const translocoService = inject(TranslocoService);
    const defaultLang = translocoService.getActiveLang();

    await firstValueFrom(translocoService.load(defaultLang));
  };
};

export const translationProviders: Provider[] = [
  provideTransloco({
    config: {
      availableLangs: [LanguageMap.German, LanguageMap.English],
      defaultLang: LanguageMap.German,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
    },
    loader: TranslocoHttpLoader,
  }),
  {
    provide: NZ_I18N,
    useFactory: provideNzI18nWithTransloco,
    deps: [TranslocoService],
  },
  {
    provide: TRANSLATION_PROVIDER,
    useExisting: TranslocoService,
  },
  {
    provide: TranslationService,
    useClass: AmlAdminDeskTranslationService,
  },
  provideSharedUiLibraryTranslation(),
];
